import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { Link, useLocation } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import {
  Loader2Icon,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Trash2
} from 'lucide-react';
import { TooltipIconButton } from '@/components/assistant-ui/tooltip-icon-button';
import CancelModal from '@/app/common/components/CancelModal';
import DataLoadError from '@/components/errors/DataLoadError';
import { errorMessage, successMessage } from '@/utils/toast';
import {
  toMcpApiErrorMessage,
  useDeleteMcpThreadMutation
} from '../api/mcpApi';
import { AI_NAV_HISTORY_LIMIT } from '../hooks/useAiAssistantNavRoutes';
import { useMcpThreadHistory } from '../hooks/useMcpThreadHistory';
import { invalidateMcpThreadHistory } from '../utils/threadHistoryEvents';
import {
  McpThreadHistoryIcon,
  McpThreadHistoryPresentation
} from './McpThreadHistoryPresentation';
import { formatThreadTitleLine } from '../utils/threadHistoryDisplay';
import { useStartNewMcpChat } from '../hooks/useStartNewMcpChat';
import { McpChatHistorySidebarPreload } from './McpChatHistorySidebarPreload';
import { McpHistoryItemMenuToggle } from './McpHistoryItemMenuToggle';

type McpChatHistorySidebarProps = {
  open: boolean;
  onToggle: () => void;
};

type PendingDelete = { id: string; title: string };

function truncateTitle(title: string | null, max = 42): string {
  const text = formatThreadTitleLine(title);
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

/** While a reply is generating server-side, refresh to clear its indicator. */
const ACTIVE_RUN_POLL_MS = 5_000;

function threadIdFromPath(pathname: string): string | undefined {
  const match = pathname.match(/^\/chat\/c\/([^/]+)\/?$/);
  return match?.[1];
}

export function McpChatHistorySidebar({
  open,
  onToggle
}: McpChatHistorySidebarProps) {
  const location = useLocation();
  const threadId = threadIdFromPath(location.pathname);
  const startNewChat = useStartNewMcpChat();

  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | false>(
    false
  );
  const [deleteThread, { isLoading: isDeleting }] =
    useDeleteMcpThreadMutation();

  const { items, loading, hasMore, total, error, reload } = useMcpThreadHistory(
    open,
    '',
    AI_NAV_HISTORY_LIMIT
  );
  const showMoreLink = hasMore || total > items.length;
  const hasActiveRun = items.some(item => item.hasActiveRun);

  useEffect(() => {
    if (!open || !hasActiveRun) return;
    const timer = window.setInterval(() => void reload(), ACTIVE_RUN_POLL_MS);
    return () => window.clearInterval(timer);
  }, [open, hasActiveRun, reload]);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const deletedId = pendingDelete.id;
    try {
      await deleteThread(deletedId).unwrap();
      invalidateMcpThreadHistory();
      successMessage('گفتگو با موفقیت حذف شد');
      if (threadId === deletedId) {
        startNewChat();
      }
    } catch (err) {
      errorMessage(toMcpApiErrorMessage(err, 'حذف ناموفق بود'));
    }
  };

  return (
    <aside
      className={`mcp-history-sidebar${open ? ' is-open' : ''}`}
      aria-hidden={!open}
    >
      <CancelModal
        show={Boolean(pendingDelete)}
        setShow={setPendingDelete}
        title="حذف گفتگو"
        submitTitle="آره، حذف بشه"
        isSubmitting={isDeleting}
        submitFunc={confirmDelete}
        buttonSize="sm"
        desc={
          <>
            آیا مطمئن هستید می‌خواهید گفتگوی{' '}
            <strong>{pendingDelete ? pendingDelete.title : ''}</strong> را حذف
            کنید؟
          </>
        }
      />

      <div className="mcp-history-sidebar-inner">
        <div className="mcp-history-sidebar-top">
          <button
            type="button"
            className="mcp-history-icon-btn"
            onClick={onToggle}
            title="بستن سابقه"
            aria-label="بستن سابقه"
          >
            <PanelLeftClose size={15} strokeWidth={2} />
          </button>
          <button
            type="button"
            className="mcp-history-new-btn"
            onClick={() => startNewChat()}
          >
            <Plus size={13} strokeWidth={2.25} />
            گفتگوی جدید
          </button>
        </div>

        <div className="mcp-history-sidebar-label">سابقه چت</div>

        <div className="mcp-history-list">
          {loading && items.length === 0 && (
            <McpChatHistorySidebarPreload rows={AI_NAV_HISTORY_LIMIT} />
          )}

          {!loading && error && (
            <div className="mcp-history-state px-2">
              <DataLoadError
                compact
                onRetry={() => void reload()}
                isRetrying={loading}
                isNetworkError={/failed to fetch|network|internet|اتصال/i.test(
                  error
                )}
              />
            </div>
          )}

          {!loading && !error && items.length === 0 && (
            <div className="mcp-history-state text-body-secondary">
              هنوز گفتگویی ذخیره نشده است.
            </div>
          )}

          {(!loading || items.length > 0) &&
            !error &&
            items.map(item => {
              const active = threadId === item.id;
              const menuOpen = menuOpenId === item.id;
              return (
                <div
                  key={item.id}
                  className={classNames('mcp-history-item', {
                    'is-active': active,
                    'is-menu-open': menuOpen
                  })}
                >
                  <Link
                    to={`/chat/c/${item.id}`}
                    className="mcp-history-item-main align-items-start"
                    title={formatThreadTitleLine(item.title)}
                  >
                    <McpThreadHistoryIcon title={item.title} size={15} />
                    <McpThreadHistoryPresentation
                      title={item.title}
                      layout="sidebar"
                    />
                    {item.hasActiveRun ? (
                      <Loader2Icon
                        className="text-primary size-3.5 shrink-0 animate-spin ms-auto align-self-center"
                        aria-label="در حال تولید پاسخ"
                      />
                    ) : null}
                  </Link>

                  <Dropdown
                    align="end"
                    show={menuOpen}
                    onToggle={next =>
                      setMenuOpenId(next ? item.id : null)
                    }
                  >
                    <Dropdown.Toggle as={McpHistoryItemMenuToggle} />
                    <Dropdown.Menu className="mcp-history-item-menu border py-1 shadow-sm">
                      <Dropdown.Item
                        className="fs--1 text-danger d-flex align-items-center gap-2 py-2"
                        onClick={event => {
                          event.preventDefault();
                          event.stopPropagation();
                          setMenuOpenId(null);
                          setPendingDelete({
                            id: item.id,
                            title: truncateTitle(item.title, 80)
                          });
                        }}
                      >
                        <Trash2 size={14} strokeWidth={2} />
                        حذف
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              );
            })}

          {showMoreLink ? (
            <Link to="/chat/history" className="mcp-history-more-link">
              بیشتر
            </Link>
          ) : null}
        </div>
      </div>
    </aside>
  );
}

export function McpHistoryToggleButton({
  open,
  onToggle
}: {
  open: boolean;
  onToggle: () => void;
}) {
  if (open) return null;
  const historyTooltip = 'نمایش سابقه چت';
  return (
    <TooltipIconButton
      tooltip={historyTooltip}
      side="bottom"
      type="button"
      variant="ghost"
      size="icon"
      className="mcp-history-icon-btn"
      onClick={onToggle}
      aria-label={historyTooltip}
    >
      <PanelLeftOpen size={15} strokeWidth={2} />
    </TooltipIconButton>
  );
}
